import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatChip, MatChipSet } from '@angular/material/chips'
import { MatIcon } from '@angular/material/icon'
import { MatSort } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { ActivatedRoute, Router } from '@angular/router'
import {
  DataContextBuilder,
  ElderBasicPaneLayoutComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderQuantityPipe,
  ElderTableComponent,
  ElderToastService,
  Filter,
  IDataContext,
  Interval,
  IsoIntervalParsePipe,
  Sort,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Instant } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { debounceTime, filter, map, tap } from 'rxjs/operators'
import { ProdOrderPriority } from '../../../../orders/prod-orders/prod-order'
import { WorkClearance } from '../../../../orders/work-orders/work-order'
import { ControlTerminalMode } from '../../../control-terminal-mode'
import { TerminalOrder } from '../terminal-order'
import { TerminalOrderFilter, TerminalOrderService } from '../terminal-order.service'

@Component({
  selector: 'app-control-terminal-orders',
  templateUrl: './control-terminal-orders.component.html',
  styleUrls: ['./control-terminal-orders.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatChipSet,
    MatChip,
    DatePipe,
    ElderTranslatePipe,
    IsoIntervalParsePipe,
    ElderQuantityPipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
  ],
})
export class ControlTerminalOrdersComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly ProdOrderPriority = ProdOrderPriority

  public readonly data: IDataContext<TerminalOrder>

  public readonly workCenterId$ = new BehaviorSubject<string>(null)
  public readonly workStationId$ = new BehaviorSubject<string>(null)
  public readonly mode$ = new BehaviorSubject<ControlTerminalMode>(ControlTerminalMode.LIVE)
  public readonly interval$ = new BehaviorSubject<Interval>(null)
  public readonly currentTime$ = new BehaviorSubject<Instant>(Instant.now())
  public readonly includeUnstartable$ = new BehaviorSubject<boolean>(false)

  private readonly toastService = inject(ElderToastService)
  private route = inject(ActivatedRoute)
  private router = inject(Router)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const terminalOrderService = inject(TerminalOrderService)
    this.data = DataContextBuilder.start<TerminalOrder>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .build(terminalOrderService)

    combineLatest([
      this.workCenterId$,
      this.workStationId$,
      this.mode$,
      this.interval$,
      this.currentTime$,
      this.includeUnstartable$,
    ])
      .pipe(
        takeUntilDestroyed(),
        tap((update) => this.log.debug(`combineLatest fired: ${JSON.stringify(update)}`)),
        filter(
          ([wcId, wsId, displayMode, interval, currentTime]) =>
            this.isValidPreviewRequest(wcId, wsId, displayMode, interval) ||
            this.isValidLiveRequest(wcId, wsId, displayMode, currentTime)
        ),
        debounceTime(50)
      )
      .subscribe(([wcId, wsId, displayMode, interval, currentTime, includeUnstartable]) => {
        switch (displayMode) {
          case ControlTerminalMode.PREVIEW:
            this.reloadOrdersPreview(wcId, wsId, interval, includeUnstartable)
            break
          case ControlTerminalMode.LIVE:
            this.reloadOrdersLive(wcId, wsId, currentTime, includeUnstartable)
            break
        }
      })

    this.route.parent.params
      .pipe(
        takeUntilDestroyed(),
        tap((parentParams) => {
          this.log.debug(`got parent params ${JSON.stringify(parentParams)}`)
        }),
        map((params) => params['workCenterId']),
        filter((id) => id)
      )
      .subscribe((workCenterId) => {
        this.workCenterId$.next(workCenterId)
      })

    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((queryParams) => {
      this.log.debug(`queryParams ${JSON.stringify(queryParams)}`)
      const workStationId = queryParams['workStationId']
      const mode = queryParams['mode']
      const terminalInterval = queryParams['terminalInterval']
      const currentTime = queryParams['currentTime']

      if (workStationId) {
        this.workStationId = workStationId
      }

      if (mode) {
        this.mode = mode
      }

      if (terminalInterval) {
        this.interval = Interval.parse(terminalInterval)
      }

      if (currentTime) {
        this.currentTime = currentTime
      }
      const includeUnstartable = coerceBooleanProperty(queryParams['includeUnstartable'])
      this.includeUnstartable$.next(includeUnstartable)
    })
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public set workCenterId(wcId: string) {
    this.workCenterId$.next(wcId)
  }

  public set workStationId(wsId: string) {
    this.workStationId$.next(wsId)
  }

  public set mode(mode: ControlTerminalMode) {
    this.mode$.next(mode)
  }

  public set interval(interval: Interval) {
    this.interval$.next(interval)
  }

  public set currentTime(currentTime: Instant) {
    this.currentTime$.next(currentTime)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openDetail(order: TerminalOrder): void {
    if (order) {
      const isCleared = this.isCleared(order)
      const hasPos = this.hasPositions(order)
      if (isCleared && hasPos) {
        this.router.navigate([order.workOrderId], {
          relativeTo: this.route,
          queryParamsHandling: 'merge',
        })
      } else if (!isCleared) {
        this.toastService.info('orders.work.uncleared')
      } else if (!hasPos) {
        this.toastService.info('terminals.configurations.no-match')
      }
    }
  }

  public determineIcon(order: TerminalOrder): string {
    if (!this.isCleared(order)) {
      return 'visibility_off'
    } else if (!this.hasPositions(order)) {
      return 'play_disabled'
    } else {
      return 'description'
    }
  }

  public determineIconColor(order: TerminalOrder): string {
    if (!this.isCleared(order)) {
      return 'warn'
    } else if (!this.hasPositions(order)) {
      return 'warn'
    } else {
      return 'primary'
    }
  }

  public isItemCountDifferent(order: TerminalOrder): boolean {
    return order.matchedItemsAgg.itemCount !== order.totalItemsAgg.itemCount
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private isCleared(order: TerminalOrder): boolean {
    return order.clearance === WorkClearance.RELEASABLE
  }

  private hasPositions(order: TerminalOrder): boolean {
    return order.matchedItemsAgg.itemCount > 0
  }

  private isValidLiveRequest(
    workCenterId: string,
    workStationId: string,
    displayMode: ControlTerminalMode,
    currentTime: Instant
  ): boolean {
    return (
      displayMode === ControlTerminalMode.LIVE && !!workCenterId && !!workStationId && !!currentTime
    )
  }

  private isValidPreviewRequest(
    workCenterId: string,
    workStationId: string,
    displayMode: ControlTerminalMode,
    scheduleInterval: Interval
  ): boolean {
    return (
      displayMode === ControlTerminalMode.PREVIEW &&
      !!workCenterId &&
      !!workStationId &&
      !!scheduleInterval
    )
  }

  private reloadOrdersPreview(
    workCenterId: string,
    workStationId: string,
    scheduleInterval: Interval,
    includeUnstartable: boolean
  ): void {
    const filters = TerminalOrderFilter.preview(
      workCenterId,
      workStationId,
      scheduleInterval,
      includeUnstartable
    )
    this.updateAndStartDataCtx(filters)
  }

  private reloadOrdersLive(
    workCenterId: string,
    workStationId: string,
    currentTime: Instant,
    includeUnstartable: boolean
  ): void {
    const filters = TerminalOrderFilter.live(
      workCenterId,
      workStationId,
      currentTime,
      includeUnstartable
    )
    this.updateAndStartDataCtx(filters)
  }

  private updateAndStartDataCtx(filters: Filter[]): void {
    if (this.data.isStarted) {
      this.data.filter.updateFilters(filters)
      this.data.reload()
    } else {
      this.data.start([new Sort('priority', 'asc')], filters)
    }
  }
}
